import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UilBars, UilTimes, UilPhone } from '@iconscout/react-unicons';
import CleaningForm from './CleaningForm';
import Logo from '../assets/Images/logo2.png';

export default function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const NAV_ITEMS = [
    { name: 'Home',     to: '/' },
    { name: 'Services', to: '/services' },
    { name: 'About',    to: '/about' },
    { name: 'Gallery',  to: '/gallery' },
    { name: 'Contact',  to: '/contact' },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-nav' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">

          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={Logo} alt="Fabiana Fabulous Cleaning Services" className="h-10 md:h-12 w-auto" />
            <div className="hidden sm:block leading-tight">
              <p className={`text-sm font-bold tracking-tight ${scrolled ? 'text-slate-900' : 'text-white'}`}>
                Fabiana Fabulous
              </p>
              <p className={`text-xs font-medium ${scrolled ? 'text-brand-600' : 'text-gold-400'}`}>
                Cleaning Services
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive(item.to)
                    ? scrolled
                      ? 'bg-brand-50 text-brand-700'
                      : 'bg-white/15 text-white'
                    : scrolled
                      ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:4258296092"
              className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                scrolled ? 'text-slate-500 hover:text-brand-600' : 'text-white/70 hover:text-white'
              }`}
            >
              <UilPhone size={15} />
              (425) 829-6092
            </a>
            <button
              onClick={() => setDrawerOpen(true)}
              className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              Get Free Quote
            </button>
          </div>

          <button
            className={`lg:hidden p-2 rounded-xl transition-colors ${
              scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <UilTimes size={22} /> : <UilBars size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed inset-y-0 right-0 w-[280px] bg-white z-50 lg:hidden shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <img src={Logo} alt="Logo" className="h-8 w-auto" />
                  <span className="text-sm font-bold text-slate-900">Fabiana Fabulous</span>
                </div>
                <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
                  <UilTimes size={18} />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 space-y-0.5">
                {NAV_ITEMS.map((item, idx) => (
                  <motion.div key={item.to} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Link
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActive(item.to) ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="px-5 py-5 space-y-3 border-t border-slate-100">
                <a href="tel:4258296092" className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  <div className="w-7 h-7 bg-brand-50 rounded-lg flex items-center justify-center">
                    <UilPhone size={14} className="text-brand-600" />
                  </div>
                  (425) 829-6092
                </a>
                <button
                  onClick={() => { setDrawerOpen(true); setMobileOpen(false); }}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold py-3 rounded-xl transition-colors"
                >
                  Get Free Quote
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50"
            />
            <motion.aside
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed inset-y-0 right-0 w-full max-w-md bg-white z-50 overflow-auto shadow-2xl flex flex-col"
            >
              <div className="sticky top-0 bg-brand-600 text-white px-6 py-5 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg text-white">Get Your Free Quote</h3>
                  <p className="text-brand-100 text-sm">Fast, free, no obligations</p>
                </div>
                <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-xl hover:bg-brand-700 transition-colors">
                  <UilTimes size={20} />
                </button>
              </div>
              <div className="flex-1 p-6">
                <div className="mb-5 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <span className="text-amber-600 text-lg">🎉</span>
                  <p className="text-sm font-semibold text-amber-800">15% off your first cleaning</p>
                </div>
                <CleaningForm />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
